/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { Decoder, Reader, tools } from 'ts-ebml';
export const FILE_TYPES = {
  audio: ['audio/mp4', 'audio/webm'],
  video: ['video/mp4;codecs=vp9,opus', 'video/mp4;codecs=vp9', 'video/mp4;codecs=av01,opus', 'video/mp4;codecs=av01', 'video/mp4', 'video/webm; codecs="vp9, opus"', 'video/webm; codecs="vp8, opus"']
};
const FALLBACK_VIDEO_CODEC = 'video/webm; codecs="vp8, opus"';
const FALLBACK_AUDIO_CODEC = 'audio/webm';
function getSupportedMimeType(isAudioOnly) {
  var _FILE_TYPES$type$find;
  const type = isAudioOnly ? 'audio' : 'video';
  const fallbackCodec = isAudioOnly ? FALLBACK_AUDIO_CODEC : FALLBACK_VIDEO_CODEC;
  return (_FILE_TYPES$type$find = FILE_TYPES[type].find(type => MediaRecorder.isTypeSupported(type))) !== null && _FILE_TYPES$type$find !== void 0 ? _FILE_TYPES$type$find : fallbackCodec;
}
function startMediaRecorder({
  stream,
  onMediaRecorderInit,
  fileSuccess,
  fileError,
  requestAudioOnly = false,
  disableInMemoryRecording
}) {
  const mimeType = getSupportedMimeType(requestAudioOnly);
  const mediaContainerType = mimeType.includes('mp4') ? 'mp4' : 'webm';
  const mediaFile = new MediaFile({
    stream: new MediaStream([...stream.getAudioTracks(), ...(requestAudioOnly ? [] : stream.getVideoTracks())]),
    fileSuccess,
    fileError,
    disableInMemoryRecording,
    mimeType
  });
  onMediaRecorderInit(mediaFile.mediaRecorder, mediaFile.stream, mediaContainerType);
  try {
    mediaFile.mediaRecorder.start(100);
  } catch (err) {
    fileError(err);
  }
}
const createEncodedStream = () => {
  let ctrl;
  const readable = new ReadableStream({
    start(controller) {
      ctrl = controller;
    }
  });
  const push = data => ctrl.enqueue(data);
  const finish = () => ctrl.close();
  return {
    finish,
    push,
    readable
  };
};
class MediaFile {
  constructor({
    stream,
    fileSuccess,
    fileError,
    disableInMemoryRecording,
    mimeType
  }) {
    this.addToChunks = e => {
      this.encodedStream.push(e.data);
      if (this.disableInMemoryRecording) {
        return;
      }
      this.chunks.push(e.data);
      if (this.isWebM) {
        const task = () => this.readAsArrayBuffer(e.data).then(buf => {
          const elms = this.decoder.decode(buf).filter(elm => elm.type !== 'unknown');
          elms.forEach(elm => {
            // There is a known bug in ts-ebml. Unfortunately, the initial matrioska specs contained
            // "Timecode" named elm, but it was renamed to "Timestamp" later on.
            // There is an open change on the lib to fix it https://github.com/legokichi/ts-ebml/pull/44
            // But since the lib is dead, its open for almost a year now, even tho its just +3/-3 lines of code.
            // We should probably look for another ebml parser lib, but for now, we just fix it here as there are not much options.
            if (elm.type === 'u') {
              if (elm.name === 'Timestamp') {
                this.reader.lastClusterTimecode = elm.value;
              } else if (elm.name === 'TimestampScale') {
                this.reader.timecodeScale = elm.value;
              }
            }
            this.reader.read(elm);
          });
        }).catch(e => {
          this.onError(e);
        });
        return this.tasks.then(() => task());
      }
    };
    this.readAsArrayBuffer = blob => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader(); // eslint-disable-line compat/compat
        reader.readAsArrayBuffer(blob);
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = ev => {
          reject(ev.error);
        };
      });
    };
    this.onError = e => {
      this.fileError(e);
    };
    // TODO: ARC-5067
    // `cleanUp` function is necessary to stop a major memory leak
    // that carries over after each recording take. This function
    // _might_ not be necessary once the reference(s) to the object
    // itself are cleared up.
    this.cleanUp = () => {
      this.fileType = null;
      this.fileSuccess = null;
      this.fileError = console.error;
      this.tasks = null;
      this.decoder = null;
      this.reader = null;
      this.chunks = null;
      this.mediaRecorder = null;
    };
    this.onStop = async () => {
      try {
        if (this.disableInMemoryRecording) {
          return;
        }
        await this.tasks;
        let blob = new Blob(this.chunks, {
          type: this.fileType
        });
        if (this.isWebM && this.chunks && this.chunks[0].type !== 'audio/ogg') {
          const webMBuf = await this.readAsArrayBuffer(blob);
          const refinedMetadataBuf = tools.makeMetadataSeekable(this.reader.metadatas, this.reader.duration, this.reader.cues);
          const body = webMBuf.slice(this.reader.metadataSize);
          blob = new Blob([refinedMetadataBuf, body], {
            type: this.fileType
          });
        }
        this.fileSuccess(blob);
      } catch (e) {
        this.onError(e);
      } finally {
        this.encodedStream.finish();
        this.cleanUp();
      }
    };
    this.encodedStream = createEncodedStream();
    this.isWebM = mimeType.includes('webm');
    this.fileType = mimeType;
    this.fileSuccess = fileSuccess;
    this.fileError = fileError;
    this.tasks = Promise.resolve();
    this.decoder = new Decoder();
    this.reader = new Reader();
    this.chunks = [];
    this.disableInMemoryRecording = disableInMemoryRecording;

    // eslint-disable-next-line compat/compat
    this.mediaRecorder = new MediaRecorder(stream, {
      mimeType: this.fileType,
      videoBitsPerSecond: 5000000
    });
    this.mediaRecorder.ondataavailable = this.addToChunks;
    this.mediaRecorder.onerror = this.onError;
    this.mediaRecorder.onstop = this.onStop;
  }
  get stream() {
    return this.encodedStream.readable;
  }
}
export { startMediaRecorder };
export default MediaFile;